import type { BoardCell, Player } from '../../types';
import { useAutoConfirm } from '../../hooks/useAutoConfirm';
import styles from './UpgradeOffer.module.css';

interface Props {
  cell: BoardCell;
  player: Player;
  currentLevel: number;
  upgradeCost: number;
  currentRent: number;
  newRent: number;
  onUpgrade: () => void;
  onSkip: () => void;
}

const LEVEL_NAMES = ['空地', '房子', '别墅', '酒店'];
const LEVEL_ICONS = ['', '🏠', '🏡', '🏨'];

export default function UpgradeOffer({
  cell, player, currentLevel, upgradeCost, currentRent, newRent, onUpgrade, onSkip,
}: Props) {
  const canAfford = player.money >= upgradeCost;

  const { countdown, cancelAutoConfirm } = useAutoConfirm({
    onAutoConfirm: onSkip, // 10秒后默认不升级
    delayMs: 10000,
    enabled: true,
  });

  const handleUpgrade = () => {
    cancelAutoConfirm();
    onUpgrade();
  };

  const handleSkip = () => {
    cancelAutoConfirm();
    onSkip();
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.dialog}>
        <div className={styles.header}>
          <span className={styles.emoji}>{cell.emoji}</span>
          <h3 className={styles.title}>{cell.name}</h3>
          <div className={styles.subtitle}>你到达了自己的地产，要升级吗？</div>
        </div>

        <div className={styles.levelDisplay}>
          <span className={styles.currentLevel}>
            {LEVEL_ICONS[currentLevel]} {LEVEL_NAMES[currentLevel]}
          </span>
          <span className={styles.arrow}>→</span>
          <span className={styles.nextLevel}>
            {LEVEL_ICONS[currentLevel + 1]} {LEVEL_NAMES[currentLevel + 1]}
          </span>
        </div>

        <div className={styles.details}>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>升级费用</span>
            <span className={styles.detailValue}>💰 ¥{upgradeCost.toLocaleString()}</span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>当前租金</span>
            <span className={styles.detailValue}>¥{currentRent.toLocaleString()}</span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>升级后租金</span>
            <span className={styles.detailValueHighlight}>¥{newRent.toLocaleString()} (+¥{(newRent - currentRent).toLocaleString()})</span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>当前余额</span>
            <span className={`${styles.detailValue} ${canAfford ? '' : styles.insufficient}`}>
              ¥{player.money.toLocaleString()}
            </span>
          </div>
        </div>

        <div className={styles.actions}>
          <button
            className={`${styles.btn} ${styles.btnUpgrade}`}
            onClick={handleUpgrade}
            disabled={!canAfford}
          >
            {canAfford ? `升级到 ${LEVEL_NAMES[currentLevel + 1]}` : '余额不足'}
          </button>
          <button
            className={`${styles.btn} ${styles.btnSkip}`}
            onClick={handleSkip}
          >
            不升级
          </button>
        </div>

        <div className={styles.countdown}>⏰ {countdown}s 后自动不升级</div>
      </div>
    </div>
  );
}
